import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Button } from 'reactstrap';
import moment from 'moment-timezone';
import _ from 'lodash';
import * as compareVersions from 'compare-versions';

import {
  validate,
  format,
  TextField,
  TextAreaField,
  attachRefToFields,
  FlyoutInputGroup,
  ReactSelectField,
} from '../Form';


function filterOutKeys(collection, ignoreKey, includeMode) {
  return _.pickBy(collection, (v, k) => includeMode ? k === ignoreKey : k !== ignoreKey);
}

function initialValuesForModuleMeta(availableModules) {
  const dynamicFields = _.flatten(_.map(availableModules, am => _.map(am.meta, metaItem => metaItem.name)));
  return _.zipObject(dynamicFields, () => null);
}

function extractMetaKeys(module) {
  return _.map(module.meta, m => m.name);
}

function sortBySemverFunction(m1, m2) {
  try {
    return compareVersions.default(m1.tag, m2.tag);
  } catch (err1) {
    // This can happen when versions aren't semvers.
    // Sort by created date in this case
    try {
      return moment(m1.created) > moment(m2.created);
    } catch (err2) {
      // Punt
      return 0;
    }
  }
}

function createMetaComponent(page, formProps, metaItem, metaItemIndex) {
  const {
    type = 0,
    label = 'Unknown',
    name = '',
    required = false,
  } = metaItem;
  const key = metaItemIndex;
  let innards = null;

  // @TODO: These codes should be put somewhere centralized. Talk with BM.
  if (type === 0) {
    // This is a "env" type field, which will just require an input box
    const fieldName = name;
    innards = (<TextField
      type='text'
      name={fieldName}
      label={label}
      required={required}
      placeholder={required ? 'Enter a value (required)' : 'Enter a value (optional)'}
      ref={attachRefToFields.bind(this, page)}
      {...formProps}
    />);
  } else if (type === 1) {
    const fieldName = name;
    innards = (<TextAreaField
      name={fieldName}
      label={label}
      required={required}
      placeholder={required ? 'Enter a value (required)' : 'Enter a value (optional)'}
      ref={attachRefToFields.bind(this, page)}
      {...formProps}
    />);
  } else {
    console.warn('Unknown type code for meta item: ', metaItem);
    return null;
  }

  return (
    <div key={key}>
      {innards}
    </div>);
}


class SensorModuleDetailCreate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pendingModule: null,
    };

    this.handlePendingModuleSelection = this.handlePendingModuleSelection.bind(this);
  }

  formatSensorModuleOptionLabel(value) {
    const {
      version,
      description = '',
      // isMostRecent = false,
    } = value;

    // {isMostRecent && <Badge color="secondary">Latest</Badge>}
    return (
      <React.Fragment>
        <span className='sensor-module-label' title={description}>{version}</span>
      </React.Fragment>
    );
  }

  formatSensorModuleGroupLabel(value) {
    const { group } = value;
    const { detail } = group;

    return <React.Fragment><span className='sensor-module-group-label' title={detail}>{detail}</span></React.Fragment>;
  }

  optionsForAvailableModules(props) {
    // We need to create a grouping of modules together that have the same name.
    // We then differentiate by tag and sort.
    const { availableModules = [] } = props;

    const moduleSelectOption = (module, mostRecent) => ({
      availableModule: module,
      label: `${module.tag}`,
      name: module.name,
      version: module.tag,
      description: module.notes,
      value: module.id,
      isMostRecent: mostRecent.id === module.id,
    });

    const groupedByType = _.groupBy(availableModules, 'type.id');

    // Now we sort the names alphabetically
    const allModuleTypes = _.keys(groupedByType);
    const sortedModuleTypes = _.sortBy(allModuleTypes, [mt => parseInt(mt.id, 10)]);

    const groupedOptions = _.map(sortedModuleTypes, (moduleTypeId) => {
      const modules = groupedByType[moduleTypeId];
      const modulesSortedBySemver = modules.sort(sortBySemverFunction);
      const typeInfo = _.isNil(modules) ? {} : _.first(modules).type;
      const mostRecent = _.last(modulesSortedBySemver);
      const versionOptions = _.map(modulesSortedBySemver, m => moduleSelectOption(m, mostRecent));

      return {
        label: typeInfo.name,
        group: typeInfo,
        options: versionOptions,
        optionCount: modules.length,
      };
    });

    return groupedOptions;
  }

  handlePendingModuleSelection(value) {
    if (_.isNil(value)) {
      return;
    }

    const { availableModule = null } = value;
    this.setState({
      pendingModule: { module: availableModule },
    });
  }

  reportError(error) {
    console.error(error); // eslint-disable-line
    this.props.handleError(error);
  }

  async validate(values) {
    // First base form validation
    let errors = {};
    try {
      await validate(this, values);
    } catch (validationErrors) {
      errors = validationErrors;
    }

    // Have to remove any that aren't either module-selection or the
    // configuration items.
    const moduleSelection = values['module-selection'];
    if (moduleSelection && moduleSelection.availableModule) {
      const metaKeys = extractMetaKeys(moduleSelection.availableModule);
      errors = _.pick(errors, ['module-selection', ...metaKeys]);
    }

    if (!_.isEmpty(errors)) {
      throw errors;
    }
  }

  prepForSubmit(formValues) {
    const applyData = {};
    const moduleSelection = formValues['module-selection'];
    const metaKeys = extractMetaKeys(moduleSelection.availableModule);
    const cleanFormValues = _.pick(formValues, metaKeys);

    _.extend(applyData, cleanFormValues);
    applyData.module_id = moduleSelection.value;

    return applyData;
  }

  render() {
    const { pendingModule = {} } = this.state;
    const { availableModules = [] } = this.props;
    const meta = pendingModule ? pendingModule.module.meta : [];
    const notes = pendingModule ? pendingModule.module.notes : null;
    const selectName = 'module-selection';
    const initialValues = {
      ...initialValuesForModuleMeta(availableModules),
      [selectName]: null,
    };

    return (
      <Formik
        initialValues={initialValues}
        ref={(c) => { this.form = c; }}
        enableReinitialize
        validate={values => this.validate(values)}
        onSubmit={async (values, { setSubmitting }) => { // eslint-disable-line
          let newValues = values;
          try {
            newValues = await format(this, values);
          } catch (err) {
            this.reportError(err);
            return;
          }

          setSubmitting(true);
          let resp;
          try {
            newValues = this.prepForSubmit(newValues);
            resp = await this.props.handleApply(newValues);
          } catch (err) {
            this.reportError(err);
          }
          setSubmitting(false);
          return resp;
        }}
        render={(formProps) => { // eslint-disable-line
          const page = this;

          return (
            <React.Fragment>
              <div className='title'>New Module</div>
              <div className='subtitle'>Choose and configure your new Sensor Module</div>
              <form onSubmit={formProps.handleSubmit}>
                <div className='form-group'>
                  <FlyoutInputGroup label='New Module' name={selectName}>
                    <ReactSelectField
                      options={this.optionsForAvailableModules(this.props)}
                      name={selectName}
                      label='Module'
                      placeholder='Choose a Module'
                      required
                      handleFormatOptionLabel={this.formatSensorModuleOptionLabel}
                      handleFormatGroupLabel={this.formatSensorModuleGroupLabel}
                      onChange={(value) => { this.handlePendingModuleSelection(value); }}
                      ref={attachRefToFields.bind(this, page)}
                      {...formProps}
                    />
                    { notes && <p className='explanation'>{notes}</p> }
                    {
                      _.map(meta, (metaItem, index) => createMetaComponent(page, formProps, metaItem, index))
                    }
                  </FlyoutInputGroup>
                </div>
              </form>
              <div className='actions'>
                <Button color='danger' onClick={this.props.handleCancel}>Cancel</Button>
                <Button color='primary' onClick={() => formProps.handleSubmit()}>Create</Button>
              </div>
            </React.Fragment>
          );
        }}
      />);
  }
}

SensorModuleDetailCreate.propTypes = {
  availableModules: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
};

SensorModuleDetailCreate.defaultProps = {
};


// eslint-disable-next-line react/no-multi-comp
class SensorModuleDetailEdit extends Component {
  constructor(props) {
    super(props);
    this.handleInstalledModuleActionSelection = this.handleInstalledModuleActionSelection.bind(this);
  }

  getAvailableSensorModuleOptions() {
    const { availableModules = [] } = this.props;

    return _.map(availableModules, am => ({
      availableModule: am,
      label: `${am.name}`,
      value: am.id,
    }));
  }

  reportError(error) {
    console.error(err); // eslint-disable-line
    this.props.handleError(error);
  }

  handleInstalledModuleActionSelection(installedModule, value) { // eslint-disable-line no-unused-vars
    // Do nothing
  }

  findUpdateVersionForInstalledModule(module, allAvailableModules) {
    // @TODO: revisit with type change
    // Look through the list of modules and look for versions that are
    // newer according primarily to the semantic version order.
    const otherModulesWithSameName = _.filter(allAvailableModules, { name: module.name });
    const currentVersion = module.tag;

    const sortedBySemver = otherModulesWithSameName.sort(sortBySemverFunction);

    const currentVersionIndex = _.findIndex(sortedBySemver, { tag: currentVersion });
    if (currentVersionIndex) {
      if (currentVersionIndex !== sortedBySemver.length - 1) {
        // This means we're not at the last version
        return _.last(sortedBySemver);
      }
    } else {
      console.warn(
        'Unexpected situation occurred where we could not find our version',
        currentVersion,
        'in the list of sorted module versions:',
        sortedBySemver
      );
    }
  }

  actionRequiresParameters(actionValue) {
    return actionValue && (actionValue.value === 'upgrade' || actionValue.value === 'update_meta');
  }

  actionsForInstalledModule(module, allAvailableModules) {
    // @TODO: Logging module cannot be removed?
    const isRequired = false;
    const actionList = [];
    const upgradeToThisModule = this.findUpdateVersionForInstalledModule(module, allAvailableModules);
    const moduleSpec = module.spec || {};

    // @TODO: constants for values here
    actionList.push({
      label: 'Issue New Credentials',
      value: 'rotate_secrets',
      explain: 'This module will be issued new credentials and be restarted. It is a good practice to "rotate" your credentials like this on a periodic basis.',
      module,
    });

    if (!_.isEmpty(moduleSpec.meta)) {
      actionList.push({
        label: 'Update Parameters',
        value: 'update_meta',
        explain: 'You may specify new configuration parameters for your module here. For security reasons, we cannot show you your existing configuration values.',
        module,
      });
    }

    if (upgradeToThisModule) {
      actionList.push({
        label: `Upgrade to version ${upgradeToThisModule.tag}`,
        value: 'upgrade',
        explain: `We have found that a newer version of this Module (${upgradeToThisModule.tag}) is available. Modules get new features and bug fixes that improve your overall experience. Upgrading will replace the existing Module with the newer one with the configuration you provice.`,
        module,
        upgradeToThisModule,
      });
    }

    if (!isRequired) {
      actionList.push({
        label: 'Remove',
        value: 'remove',
        explain: 'This will remove the module from the Sensor completely. Use this when you no longer require this functionality.',
        module,
      });
    }

    return actionList;
  }

  async validate(values) {
    // First base form validation
    let errors = {};
    try {
      await validate(this, values);
    } catch (validationErrors) {
      errors = validationErrors;
    }

    const moduleAction = values['action-selection'];

    // Then strip out any errors that could be left behind from a change of action
    // For example... you switch to an action which requires parameters but then
    // change back. Ideally, you'd like to leave those parameters there if you change
    // your mind.
    //
    // Also.. Formik doesn't really make it clean to remove fields that I can see, so
    // this just works out all-around.
    if (!this.actionRequiresParameters(moduleAction)) {
      errors = filterOutKeys(errors, 'action-selection', true);
    }

    if (!_.isEmpty(errors)) {
      throw errors;
    }
  }

  prepForSubmit(formData) {
    const { module } = this.props;
    const values = filterOutKeys(formData, 'action-selection');
    const moduleAction = formData['action-selection'];

    let moduleActionValue = '';
    if (moduleAction) {
      moduleActionValue = moduleAction.value;
    }

    const moduleStruct = {
      action: moduleActionValue,
      module_uuid: module.uuid,
      data: {},
    };

    switch (moduleActionValue) {
      case 'remove': {
        // Nothing here... the action value will tell us what we need to do
        break;
      }
      case 'upgrade': {
        // We need to send along the new parameter values along with this module
        _.extend(moduleStruct.data, values);
        // Use the moduleId of the new module
        moduleStruct.data.module_id = moduleAction.upgradeToThisModule.id;
        break;
      }
      case 'update_meta': {
        // We need to send along the new parameter values along with this module
        _.extend(moduleStruct.data, values);
        // Use the moduleId of the existing module
        moduleStruct.data.module_id = module.module_id;
        break;
      }
      case 'rotate_secrets': {
        // By setting this to true, it instructs the backend to take care of this
        moduleStruct.data.rotate_secrets = true;
        moduleStruct.data.module_id = module.module_id;
        break;
      }
      default:
        break;
    }

    return moduleStruct;
  }

  render() {
    const { module = {}, availableModules = [] } = this.props;
    const version = module.tag;
    const selectName = 'action-selection';
    const actions = this.actionsForInstalledModule(module, availableModules);
    const defaultAction = actions.length ? actions[0] : null;
    // You have to make initial values for each of the meta fields or Formik won't
    // do much of anything properly for you. Symptoms include:
    //   - no auto-touching fields on submit
    //   - validation errors don't always show up the way you expect
    const initialValues = {
      ...initialValuesForModuleMeta(availableModules),
      [selectName]: defaultAction,
    };
    const moduleSpec = module.spec || {};
    const meta = moduleSpec.meta || [];

    return (
      <Formik
        initialValues={initialValues}
        ref={(c) => { this.form = c; }}
        enableReinitialize
        validate={values => this.validate(values)}
        onSubmit={async (values, { setSubmitting }) => { // eslint-disable-line
          let newValues = values;
          try {
            newValues = await format(this, values);
          } catch (err) {
            this.reportError(err);
            return;
          }

          setSubmitting(true);
          let resp;
          try {
            newValues = this.prepForSubmit(newValues);
            resp = await this.props.handleApply(newValues);
          } catch (err) {
            this.reportError(err);
          }
          setSubmitting(false);
          return resp;
        }}
        render={(formProps) => { // eslint-disable-line
          const page = this;
          const actionValue = formProps.values[selectName];
          const showMeta = this.actionRequiresParameters(actionValue);

          return (
            <React.Fragment>
              <div className='title'>{module.name}</div>
              <div className='subtitle'>{version} {module.notes}</div>
              <form onSubmit={formProps.handleSubmit}>
                <div className='form-group'>
                  <FlyoutInputGroup name={selectName}>
                    <ReactSelectField
                      options={actions}
                      name={selectName}
                      defaultValue={defaultAction}
                      label='Action to Take'
                      placeholder='Choose an Action'
                      required={false}
                      onChange={(value) => { this.handleInstalledModuleActionSelection(module, value); }}
                      ref={attachRefToFields.bind(this, page)}
                      {...formProps}
                    />
                    <p className='explanation'>
                      { actionValue.explain }
                    </p>
                    { showMeta &&
                      _.map(meta, (metaItem, index) => createMetaComponent(page, formProps, metaItem, index))
                    }
                  </FlyoutInputGroup>
                </div>
              </form>
              <div className='actions'>
                <Button color='danger' onClick={this.props.handleCancel}>Cancel</Button>
                <Button color='primary' onClick={() => formProps.handleSubmit()}>Apply Changes</Button>
              </div>
            </React.Fragment>
          );
        }}
      />);
  }
}

SensorModuleDetailEdit.propTypes = {
  module: PropTypes.shape({}).isRequired,
  availableModules: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
};

SensorModuleDetailEdit.defaultProps = {
};


// eslint-disable-next-line react/no-multi-comp
const SensorModuleDetail = (props) => {
  const {
    mode,
    sensor,
    module = {},
    handleApply,
    handleCancel,
    handleError,
  } = props;
  const { availableModules = [] } = sensor;

  if (mode === 'create') {
    return (
      <SensorModuleDetailCreate
        handleApply={handleApply}
        handleCancel={handleCancel}
        handleError={handleError}
        availableModules={availableModules}
      />);
  }

  if (mode === 'edit') {
    return (
      <SensorModuleDetailEdit
        handleApply={handleApply}
        handleCancel={handleCancel}
        handleError={handleError}
        module={module}
        availableModules={availableModules}
      />);
  }
};

SensorModuleDetail.propTypes = {
  mode: PropTypes.string,
  sensor: PropTypes.shape({}).isRequired,
  module: PropTypes.shape({}),
  handleCancel: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
};

SensorModuleDetail.defaultProps = {
  mode: 'edit',
  module: {},
};

export default SensorModuleDetail;



// WEBPACK FOOTER //
// ./src/views/Components/Detail/SensorModuleDetail.js